import { computed, ref } from 'vue';
import { faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { adminTermsClient } from '~/services';
import type { Term, TermFormData, TermPayload } from '~/services/adminTermsClient.types';

const DEFAULT_CONSENT_TEXT = 'I have read and agree to the Terms and Conditions.';
const DEFAULT_CONSENT_BUTTON = 'Accept';

const defaultData = (): TermFormData => ({
    title: '',
    description: null,
    content: '',
    consent_text: DEFAULT_CONSENT_TEXT,
    consent_button: DEFAULT_CONSENT_BUTTON,
    effective_at: '',
    org_unit_coverage: 'none',
    org_units: [],
});

const populateFromTerm = (term: Term | null): TermFormData =>
    term
        ? {
              title: term.title,
              description: term.description,
              content: term.content ?? '',
              consent_text: term.consent_text,
              consent_button: term.consent_button,
              effective_at: term.effective_at,
              org_unit_coverage: term.org_unit_coverage,
              org_units: term.org_units ?? [],
          }
        : defaultData();

const toPayload = ({ org_units, ...data }: TermFormData): TermPayload => ({
    ...data,
    org_unit_ids: data.org_unit_coverage === 'selected' ? org_units.map((orgUnit) => orgUnit.id) : [],
});

export function useTermFormSubmission(teamId: number, term: Term | null) {
    const data = ref<TermFormData>(populateFromTerm(term));
    const backHref = `/kiosk/teams/${teamId}/terms`;
    const isEditing = term !== null;
    const createdTermId = ref<number | null>(null);

    const { submit, processing } = useFormSubmission({
        data,
        submit: async () => {
            if (term) {
                return adminTermsClient.updateTerm(teamId, term.id, toPayload(data.value));
            }

            const response = await adminTermsClient.createTerm(teamId, toPayload(data.value));
            createdTermId.value = response.data.id;

            return response;
        },
        reset: () => {
            if (!term) {
                data.value = defaultData();
            }
        },
        notification: () => ({
            message: isEditing ? 'Terms updated' : 'Terms created',
            ...(isEditing ? {} : { redirect: `${backHref}/${createdTermId.value}` }),
            action: {
                icon: faSquareArrowUpLeft,
                label: 'Back to Terms',
                href: backHref,
            },
        }),
        preserveScroll: false,
    });

    const loadActiveTerm = async (): Promise<Term | null> => {
        const { data: page } = await adminTermsClient.getTerms(teamId, { status: ['published'], per_page: 1 });
        const active = page.data[0];

        if (!active || active.id === term?.id) {
            return null;
        }

        const { data: activeTerm } = await adminTermsClient.getTerm(teamId, active.id);

        return activeTerm;
    };

    const canEdit = computed(() => !term || term.status === 'draft');
    const canPublish = computed(() => term !== null && term.status === 'draft');

    const publish = () => {
        if (term === null) {
            return Promise.resolve();
        }

        return adminTermsClient.updateTermStatus(teamId, term.id, 'published').then(() => {
            window.location.assign(backHref);
        });
    };

    return {
        data,
        submit,
        processing,
        canEdit,
        canPublish,
        publish,
        backHref,
        isEditing,
        loadActiveTerm,
    };
}
